import styles from "./MikeMessage.module.less";
import { Typography, Image, Skeleton, Spin, Card } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import MikeTitleParameters from "./components/mikeTitleParameters/MikeTitleParameters";
import {
  CopilotResponseActionType,
  MikeMessageParameters,
  HealtProcessSummaryResponseParameters,
  ThermalLiDARResponseParameters,
} from "~/pages/copilotMessage/types/copilot";
import { parseMikeMessageParameters } from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/helpers/mikeMessageParametersHelper";
import IRScanAnalysis from "./components/irScanAnalysis/IRScanAnalysis";
import LadleHealtProcessSummary from "./components/ladleHealtProcessSummary/LadleHealtProcessSummary";
import LaserScanAnalysis from "./components/laserScanAnalysis/LaserScanAnalysis";
import { addSequentialLoadingListener } from "./helpers/sequentialLoadingSystem";
import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import FeedbackSection from "./components/feedbackSection/FeedbackSection";
import { FeedbackSummary } from "~/types/feedback";
import { useLanguage } from "~/hooks/useLanguage";

const SKELETON_ROWS = {
  [CopilotResponseActionType.LaserScan]: 18,
  [CopilotResponseActionType.ThermalScan]: 18,
  [CopilotResponseActionType.CampaignCreated]: 5,
  [CopilotResponseActionType.HealtProcessSummary]: 10,
};

interface MikeMessageProps<T extends MikeMessageParameters> {
  id: string;
  type: CopilotResponseActionType;
  parameters?: T;
  operationDate?: string;
  greetingMessages?: string[];
  observationMessages?: string[];
  checkMessages?: { [key: string]: string };
  recommendationMessages?: string[];
  forceRenderSignal?: number; // 0: re-render when not initialized, >0: clear component when initialized
  onVisibleChanged: (target: Element) => void;
  conversationId: string;
  feedbackSummary?: FeedbackSummary;
}

function MikeMessage<T extends MikeMessageParameters>({
  id,
  type,
  parameters,
  operationDate,
  greetingMessages,
  observationMessages,
  checkMessages,
  recommendationMessages,
  forceRenderSignal,
  conversationId,
  feedbackSummary,
  onVisibleChanged,
}: MikeMessageProps<T>) {
  const mikeElementRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout>();

  const [initialized, setInitialized] = useState(false);
  const initializedRef = useRef(initialized);
  initializedRef.current = initialized;

  const [renderedLength, setRenderedLength] = useState(0);

  const { formatMessage } = useLanguage();

  useEffect(() => {
    if (forceRenderSignal && initializedRef.current) {
      setInitialized(false);
    } else if (!initializedRef.current && forceRenderSignal === 0) {
      setInitialized(true);
    }
  }, [forceRenderSignal]);

  const combinedGreetingMessages = useMemo(
    () => ((greetingMessages?.length ?? 0) ? greetingMessages?.join(" ") : ""),
    [greetingMessages],
  );

  const combinedObservationMessages = useMemo(
    () =>
      (observationMessages?.length ?? 0) ? observationMessages?.join(" ") : "",
    [observationMessages],
  );

  const hasMessage = useMemo(
    () =>
      combinedGreetingMessages ||
      combinedObservationMessages ||
      (recommendationMessages?.length ?? 0) > 0,
    [
      combinedGreetingMessages,
      combinedObservationMessages,
      recommendationMessages,
    ],
  );

  const title = useMemo(() => {
    switch (type) {
      case CopilotResponseActionType.CampaignCreated:
        return formatMessage("MikeMessage.CampaignCreated");

      case CopilotResponseActionType.ThermalScan:
        return formatMessage("MikeMessage.IRScanAnalysis");

      case CopilotResponseActionType.LaserScan:
        return formatMessage("MikeMessage.LaserScanAnalysis");

      default:
        return formatMessage("MikeMessage.LadleHealthProcessSummary");
    }
  }, [type, formatMessage]);

  useEffect(() => {
    const mikeInitializedChanged = (e: Event) => {
      onVisibleChanged(e.target as Element);
    };

    const mikeElement = mikeElementRef.current;
    mikeElement?.addEventListener(
      "mike-visible-changed",
      mikeInitializedChanged,
    );

    const initializeListener = addSequentialLoadingListener(id, () => {
      setInitialized(true);
    });

    return () => {
      initializeListener();

      mikeElement?.removeEventListener(
        "mike-visible-changed",
        mikeInitializedChanged,
      );
    };
  }, [id, onVisibleChanged]);

  const checkMessagesArray = useMemo(() => {
    if (!checkMessages) return [];

    return Object.keys(checkMessages)
      .map((key) => {
        return {
          key,
          value: checkMessages[key],
        };
      })
      .sort((a, b) => {
        return a.value && !b.value
          ? -1
          : !a.value && b.value
            ? 1
            : a.key < b.key
              ? -1
              : 1;
      });
  }, [checkMessages]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (initialized) {
      intervalRef.current = setInterval(() => {
        if (checkMessagesArray.length > renderedLength) {
          setRenderedLength((prev) => prev + 1);
        } else {
          if (intervalRef.current != undefined)
            clearInterval(intervalRef.current);
        }
      }, 650);
    }
  }, [initialized, renderedLength, checkMessagesArray.length]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      ref={mikeElementRef}
      className={`${styles.MikeMessage} mike-item`}
      data-mike-id={id}
      data-mike-initialized={initialized}
      data-mike-visible={false}
      data-mike-preparing={false}
    >
      {initialized ? (
        <>
          <div className={styles.messageHeader}>
            <div className={styles.mikeLogo}>
              <Image
                className={styles.image}
                preview={false}
                height={50}
                src="/images/mike.opac.png"
              />
            </div>

            <Typography.Text className={styles.date}>
              {operationDate}
            </Typography.Text>
          </div>

          <div className={styles.messageCardWrapper}>
            <Card className={styles.messageBox}>
              <div className={styles.mikeLogoArrowWrapper}>
                <div className={styles.mikeLogoArrow}></div>
              </div>

              <div className={styles.titleWrapper}>
                <Typography.Title level={2} className={styles.title}>
                  {title}
                </Typography.Title>
                <MikeTitleParameters
                  type={type}
                  parameters={parameters}
                  conversationId={conversationId}
                />
              </div>
              {hasMessage ? (
                <div className={styles.textWrapper}>
                  {combinedGreetingMessages && (
                    <Typography.Title level={2} className={styles.text}>
                      {combinedGreetingMessages}
                    </Typography.Title>
                  )}

                  {(recommendationMessages?.length ?? 0) > 0 && (
                    <>
                      <Typography.Title level={2} className={styles.text}>
                        {formatMessage("MikeMessage.HereAreMySuggestions")}
                      </Typography.Title>
                      <ul className={styles.recommendationList}>
                        {recommendationMessages?.map((x, index) => (
                          <li key={index + id}>
                            <Typography.Text className={styles.text}>
                              {x}
                            </Typography.Text>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {checkMessagesArray.length > 0 && (
                    <div>
                      <Typography.Title level={2} className={styles.text}>
                        {formatMessage("MikeMessage.HereAreChecks")}
                      </Typography.Title>

                      <ul style={{ margin: 1 }}>
                        {checkMessagesArray.map(({ key, value }, index) => (
                          <li
                            style={{
                              listStyle: "none",
                              alignItems: "center",
                              visibility:
                                renderedLength >= index ? "visible" : "hidden",
                            }}
                            key={key}
                          >
                            <div className={styles.checkMessage}>
                              <Typography.Title
                                level={2}
                                style={{ paddingRight: 6 }}
                              >
                                {value ? (
                                  <CloseOutlined style={{ color: "#e74c3c" }} />
                                ) : (
                                  <CheckOutlined style={{ color: "#2ecc71" }} />
                                )}
                              </Typography.Title>

                              <Typography.Title
                                level={2}
                                style={{
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {key}
                              </Typography.Title>

                              <Typography.Title
                                level={2}
                                style={{
                                  color: value ? "#e74c3c" : "#2ecc71",
                                  whiteSpace: "nowrap",
                                  paddingLeft: 4,
                                  fontWeight: value ? 600 : undefined,
                                }}
                              >
                                {value ? "(Issue Detected):" : ""}
                              </Typography.Title>

                              {value && (
                                <Typography.Title
                                  level={2}
                                  style={{
                                    fontStyle: "italic",
                                    color: "#636e72",
                                    paddingLeft: 4,
                                  }}
                                >
                                  {value}
                                </Typography.Title>
                              )}
                            </div>
                          </li>
                        ))}
                        {renderedLength < checkMessagesArray.length && (
                          <li
                            style={{
                              listStyle: "none",
                              alignItems: "center",
                            }}
                          >
                            <Spin
                              className={styles.checkMessage}
                              indicator={<LoadingOutlined spin />}
                            />
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {combinedObservationMessages &&
                    renderedLength >= checkMessagesArray.length && (
                      <Typography.Title level={2} className={styles.text}>
                        {combinedObservationMessages}
                      </Typography.Title>
                    )}
                </div>
              ) : null}
            </Card>

            <FeedbackSection
              messageId={id}
              conversationId={conversationId}
              feedbackSummary={feedbackSummary}
            />
          </div>

          {type === CopilotResponseActionType.ThermalScan && parameters && (
            <IRScanAnalysis
              parameters={parseMikeMessageParameters<ThermalLiDARResponseParameters>(
                type,
                parameters,
              )}
            />
          )}

          {type === CopilotResponseActionType.HealtProcessSummary &&
            parameters && (
              <LadleHealtProcessSummary
                parameters={parseMikeMessageParameters<HealtProcessSummaryResponseParameters>(
                  type,
                  parameters,
                )}
              />
            )}

          {type === CopilotResponseActionType.LaserScan && parameters && (
            <LaserScanAnalysis
              parameters={parseMikeMessageParameters<ThermalLiDARResponseParameters>(
                type,
                parameters,
              )}
            />
          )}
        </>
      ) : (
        <Skeleton
          active
          paragraph={{
            rows: SKELETON_ROWS[type],
          }}
        />
      )}
    </div>
  );
}

export default MikeMessage;
